import React, { useState, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Search,
  PlusCircle,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  useBooks,
  useCatalogStatistics,
  useDeleteBook,
  useAuthors,
  useGenres,
} from '@/hooks/catalog-hooks';
import type { Book, BookQueryParams } from '@/services/catalog-api';
import { AddBookWithInventoryForm, EditBookForm } from '@/components/admin/components';
import { useDensity } from '@/components/admin/context/DensityContext';

const BooksModule: React.FC = () => {
  const { toast } = useToast();
  const { density } = useDensity();

  // Fixed page size for books listing
  const FIXED_PAGE_SIZE = 10;

  // API Query state (page_size locked to 10)
  const [queryParams, setQueryParams] = useState<BookQueryParams>({
    page: 1,
    page_size: FIXED_PAGE_SIZE,
    ordering: 'title'
  });

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [sortBy, setSortBy] = useState('title');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | null>(null);

  // API Hooks
  const {
    data: booksResponse,
    isLoading: isLoadingBooks,
    isError: isBooksError,
    error: booksError,
    refetch: refetchBooks
  } = useBooks(queryParams);

  const {
    data: statistics,
    isLoading: isLoadingStats
  } = useCatalogStatistics();

  // Get all authors for counting (first page only for stats)
  const { data: authorsResponse } = useAuthors({ page_size: 1 });

  // Get all genres for filtering options
  const { data: genresResponse } = useGenres({ page_size: 100 });

  // Get all books with availability filter for accurate count
  const { data: availableBooksResponse } = useBooks({
    page_size: 1,
    availability: 'available'
  });

  const deleteBookMutation = useDeleteBook();

  // Extract data from API response
  const books = booksResponse?.results || [];
  const totalBooks = booksResponse?.count || 0;
  const totalAuthors = authorsResponse?.count || 0;
  const availableGenres = genresResponse?.results || [];
  const totalAvailableBooks = availableBooksResponse?.count || 0;

  // Get unique categories, genres, and languages from current books
  const uniqueData = useMemo(() => {
    // Use API genres for filter options instead of just current books
    const categories = availableGenres.length > 0
      ? availableGenres.map(genre => genre.name).sort()
      : [...new Set(books.flatMap(book =>
          book.genres.map(genre => genre.name)
        ))].sort();

    const languages = [...new Set(books.map(book => book.language))].sort();

    return { categories, languages };
  }, [books, availableGenres]);

  // Update query parameters when filters change
  const updateQueryParams = (updates: Partial<BookQueryParams>) => {
    setQueryParams(prev => ({
      ...prev,
      ...updates,
      // Always enforce fixed page size
      page_size: FIXED_PAGE_SIZE,
      // If caller provided an explicit `page`, preserve it; otherwise reset to 1
      page: updates.page ?? 1,
    }));
  };

  // Handle search with debouncing
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        updateQueryParams({ search: searchQuery });
      } else {
        updateQueryParams({ search: undefined });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle filter changes
  React.useEffect(() => {
    const updates: Partial<BookQueryParams> = {};

    if (languageFilter !== 'all') {
      updates.language = languageFilter;
    } else {
      updates.language = undefined;
    }

    // Handle category/genre filter
    if (categoryFilter !== 'all') {
      updates.genre_name = categoryFilter;
    } else {
      updates.genre_name = undefined;
    }

    // Handle status filter (client-side for now since API might not support this directly)
    if (statusFilter === 'available' || statusFilter === 'unavailable') {
      updates.availability = statusFilter;
    } else {
      updates.availability = undefined;
    }

    updateQueryParams(updates);
  }, [languageFilter, statusFilter, categoryFilter]);

  // Handle sorting changes
  React.useEffect(() => {
    const orderingMap: Record<string, string> = {
      'title': 'title',
      'author': 'authors__last_name',
      'publication_date': '-publication_date',
      'rating': '-average_rating',
      'created': '-created_at'
    };

    updateQueryParams({ ordering: orderingMap[sortBy] || 'title' });
  }, [sortBy]);

  const handleAddBookSuccess = () => {
    refetchBooks();
    toast({
      title: 'Success',
      description: 'Book added successfully'
    });
  };

  const handleEditBookSuccess = () => {
    refetchBooks();
    toast({
      title: 'Success',
      description: 'Book updated successfully'
    });
  };

  const handleDeleteBook = async (bookId: number) => {
    try {
      await deleteBookMutation.mutateAsync(bookId);
      // The hook handles success toast and cache invalidation
    } catch (error) {
      // Error is handled by the mutation hook
      console.error('Failed to delete book:', error);
    }
  };

  const openEditDialog = (book: Book) => {
    setEditingBook(book);
    setIsEditDialogOpen(true);
  };

  const currentPage = queryParams.page || 1;
  const totalPages = booksResponse ? Math.ceil(booksResponse.count / FIXED_PAGE_SIZE) : 1;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Books</h1>
          <p className="text-sm text-gray-500 mt-1">Manage library catalog and physical copies.</p>
        </div>
        <button
          onClick={() => setIsAddDialogOpen(true)}
          className="bg-amber-500 hover:bg-amber-600 text-white font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          Add Book
        </button>
      </div>

      {/* Stats row: 4 cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Books', value: (statistics?.total_books ?? totalBooks ?? 0).toLocaleString() },
          { label: 'Available', value: (totalAvailableBooks ?? 0).toLocaleString() },
          { label: 'Total Authors', value: (statistics?.total_authors ?? totalAuthors ?? 0).toLocaleString() },
          { label: 'Total Genres', value: (statistics?.total_genres ?? genresResponse?.count ?? 0).toLocaleString() },
        ].map(s => (
          <div key={s.label} className="bg-white border border-[#E8E4DC] rounded-xl px-5 py-4">
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-gray-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search books..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <span className="text-xs text-gray-400 font-mono ml-auto">{totalBooks ?? 0} records</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8F7F5] border-b border-[#E8E4DC]">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Title</th>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Author</th>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Genre</th>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Rating</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {isLoadingBooks ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-3/4" /></td>
                  <td className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-1/2" /></td>
                  <td className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-1/3" /></td>
                  <td className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-1/4" /></td>
                  <td className="px-5 py-4" />
                </tr>
              ))
            ) : books.length > 0 ? (
              books.map(book => (
                <tr key={book.id} className="hover:bg-[#FFFBEB] transition-colors group">
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm font-medium text-gray-900`}>
                    {book.title}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-gray-500`}>
                    {book.authors?.map((a: any) => a.full_name || a.name).join(', ') ?? '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-gray-500`}>
                    {book.genres?.map((g: any) => g.name).join(', ') ?? '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-gray-500`}>
                    {book.average_rating ? `${Number(book.average_rating).toFixed(1)} ★` : '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                      <button
                        onClick={() => openEditDialog(book)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteBook(book.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete"
                        disabled={deleteBookMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-400">
                  {queryParams.search ? 'No books found matching your search.' : 'No books available.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Pagination */}
        <div className="px-5 py-3 border-t border-[#E8E4DC] flex items-center justify-between">
          <span className="text-xs text-gray-400 font-mono">Page {currentPage} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => updateQueryParams({ page: Math.max(1, currentPage - 1) })}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => updateQueryParams({ page: Math.min(totalPages, currentPage + 1) })}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Drawers */}
      <AddBookWithInventoryForm
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSuccess={handleAddBookSuccess}
      />
      <EditBookForm
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingBook(null);
        }}
        book={editingBook}
        onSuccess={handleEditBookSuccess}
      />
    </div>
  );
};

export default BooksModule;
